import _ from "underscore";
import { mobileAlert, hideLoader, hideUnobtrusiveLoader, showLoader, showUnobtrusiveLoader } from "../plugins";
import { createAsyncAction } from "../utils/ajex";
import * as TYPES from "./types";
import * as SurveysApi from "../api/surveys";
import * as aj from "../aj/index";
import M from "../strings";
import * as _query from "../framework/query";
import { isNotEmpty } from "../framework/utils";
import { SURVEY_STATUS } from "../model/vars";
import * as responses from "../api/responses";

export const getOpenSurveys = createAsyncAction(TYPES.GET_OPEN_SURVEYS, data =>{
    const query = isNotEmpty(data.query) ? data.query : _query.create();

    aj.dispatch({
        type: TYPES.GET_OPEN_SURVEYS,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.getSurveys({status: SURVEY_STATUS.OPEN}, query.cleaned())
            .then((response) => {
                getOpenSurveys.complete({
                    openSurveys: response.value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                getOpenSurveys.fail();
                reject();
            })
            .finally(hideLoader)
    })
});

export const getUserSurveys = createAsyncAction(TYPES.GET_USER_SURVEYS, data =>{
    const query = isNotEmpty(data.query) ? data.query : _query.create();

    aj.dispatch({
        type: TYPES.GET_USER_SURVEYS,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.getSurveys({user: "me"}, query.cleaned())
            .then((response) => {
                getUserSurveys.complete({
                    userSurveys: response.value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                getUserSurveys.fail();
                reject();
            })
            .finally(hideLoader)
    })
});

export const createSurvey = createAsyncAction(TYPES.CREATE_SURVEY, data => {
    aj.dispatch({
        type: TYPES.CREATE_SURVEY,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.createSurvey(data)
            .then(() => {
                createSurvey.complete();
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                createSurvey.fail();
                reject();
            })
            .finally(hideLoader)
    })
})

export const loadSurvey = createAsyncAction(TYPES.LOAD_SURVEY, data => {
    aj.dispatch({
        type: TYPES.LOAD_SURVEY,
    })

    const promises = [
        SurveysApi.getQuestionsTree(data),
        SurveysApi.getSurveyScores(data),
        SurveysApi.getSurveyRatings(data),
        SurveysApi.getSurveyUsers(data),
    ];

    showLoader();
    return Promise.all(promises)
        .then(responses => {
            const [questionsTree, surveyScores, surveyRatings, surveyUsers] = responses;
            loadSurvey.complete({
                questionsTree: questionsTree.value,
                surveyScores: surveyScores.value,
                surveyRatings: surveyRatings.value,
                surveyUsers: surveyUsers.value,
            });
        })
        .catch(e => {
            mobileAlert(M("ooops"), responses.msg(e), "error");
            loadSurvey.fail();
            throw new Error(e);
        })
        .finally(hideLoader)
})

export const clearSurvey = aj.createAction(TYPES.CLEAR_SURVEY, data => {
    aj.dispatch({
        type: TYPES.CLEAR_SURVEY,
    })
})

export const getQuestionsTree = createAsyncAction(TYPES.GET_QUESTIONS_TREE, data => {
    aj.dispatch({
        type: TYPES.GET_QUESTIONS_TREE,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.getQuestionsTree(data)
            .then((response) => {
                getQuestionsTree.complete({
                    questionsTree: response.value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                getQuestionsTree.fail();
                reject();
            })
            .finally(hideLoader)
    })
})

export const getSurveyRatings = createAsyncAction(TYPES.GET_SURVEY_RATINGS, data => {
    aj.dispatch({
        type: TYPES.GET_SURVEY_RATINGS,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.getSurveyRatings(data)
            .then((response) => {
                getSurveyRatings.complete({
                    surveyRatings: response.value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                getSurveyRatings.fail();
                reject();
            })
            .finally(hideLoader)
    })
})

export const setSurveyRating = createAsyncAction(TYPES.SET_SURVEY_RATING, data => {
    aj.dispatch({
        type: TYPES.SET_SURVEY_RATING,
    })

    return new Promise((resolve, reject) => {
        showUnobtrusiveLoader();
        SurveysApi.setSurveyRating(data)
            .then((response) => {
                const value = response.value,
                    {ratings, scores} = value;
                setSurveyRating.complete({
                    surveyRatings: ratings,
                    surveyScores: scores,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                setSurveyRating.fail();
                reject();
            })
            .finally(hideUnobtrusiveLoader)
    })
})

export const setSurveyRatingNotes = createAsyncAction(TYPES.SET_SURVEY_RATING_NOTES, data => {
    aj.dispatch({
        type: TYPES.SET_SURVEY_RATING_NOTES,
    })

    return new Promise((resolve, reject) => {
        showUnobtrusiveLoader();
        SurveysApi.setSurveyRatingNotes(data)
            .then(() => SurveysApi.getSurveyRatings({id: data.surveyId}))
            .then((response) => {
                const value = response.value;
                setSurveyRatingNotes.complete({
                    surveyRatings: value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                setSurveyRatingNotes.fail();
                reject();
            })
            .finally(hideUnobtrusiveLoader)
    })
})

export const getSurveyScores = createAsyncAction(TYPES.GET_SURVEY_SCORES, data => {
    aj.dispatch({
        type: TYPES.GET_SURVEY_SCORES,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.getSurveyScores(data)
            .then((response) => {
                getSurveyScores.complete({
                    surveyScores: response.value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                getSurveyScores.fail();
                reject();
            })
            .finally(hideLoader)
    })
})

export const getSurveyUsers = createAsyncAction(TYPES.GET_SURVEY_USERS, data => {
    aj.dispatch({
        type: TYPES.GET_SURVEY_USERS,
    })

    return new Promise((resolve, reject) => {
        //showLoader();
        SurveysApi.getSurveyUsers(data)
            .then((response) => {
                getSurveyUsers.complete({
                    surveyUsers: response.value,
                });
                resolve();
            })
            .catch(e => {
                //mobileAlert(M("ooops"), responses.msg(e), "error");
                getSurveyUsers.fail();
                reject();
            })
            //.finally(hideLoader)
    })
})

export const closeSurvey = createAsyncAction(TYPES.CLOSE_SURVEY, data => {
    aj.dispatch({
        type: TYPES.CLOSE_SURVEY,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.closeSurvey(data)
            .then(() => loadSurvey(data))
            .then(() => {
                closeSurvey.complete({});
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                closeSurvey.fail();
                reject();
            })
            .finally(hideLoader)
    })
})

export const reopenSurvey = createAsyncAction(TYPES.REOPEN_SURVEY, data => {
    aj.dispatch({
        type: TYPES.REOPEN_SURVEY,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.reopenSurvey(data)
            .then(() => loadSurvey(data))
            .then(() => {
                reopenSurvey.complete({});
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                reopenSurvey.fail();
                reject();
            })
            .finally(hideLoader)
    })
})

export const addRatingPhotos = createAsyncAction(TYPES.ADD_RATING_PHOTOS, data => {
    aj.dispatch({
        type: TYPES.ADD_RATING_PHOTOS,
    })

    return new Promise((resolve, reject) => {
        showUnobtrusiveLoader();
        _.reduce(data.pictures, (prev,curr) => {
                return prev.then(() => {
                    return SurveysApi.addPhoto(_.extend(
                        _.pick(data, "surveyId", "questionId"),
                        {file: curr}
                    ))
                })
            }, Promise.resolve())
            .then(() => SurveysApi.getSurveyRatings({id: data.surveyId}))
            .then((response) => {
                const value = response.value;
                addRatingPhotos.complete({
                    surveyRatings: value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                addRatingPhotos.fail();
                reject();
            })
            .finally(hideUnobtrusiveLoader)
    })
})

export const deleteRatingPhotos = createAsyncAction(TYPES.DELETE_RATING_PHOTOS, data => {
    aj.dispatch({
        type: TYPES.DELETE_RATING_PHOTOS,
    })

    return new Promise((resolve, reject) => {
        showUnobtrusiveLoader();
        _.reduce(data.pictures, (prev,curr) => {
                return prev.then(() => {
                    return SurveysApi.removePhoto(_.extend(
                        _.pick(data, "surveyId", "ratingId"),
                        {photoId: curr.uuid}
                    ))
                })
            }, Promise.resolve())
            .then(() => SurveysApi.getSurveyRatings({id: data.surveyId}))
            .then((response) => {
                const value = response.value;
                deleteRatingPhotos.complete({
                    surveyRatings: value,
                });
                resolve();
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                deleteRatingPhotos.fail();
                reject();
            })
            .finally(hideUnobtrusiveLoader)
    })
})

export const getSurveysStats = createAsyncAction(TYPES.GET_SURVEYS_STATS, data =>{
    aj.dispatch({
        type: TYPES.GET_SURVEYS_STATS,
    })

    return new Promise((resolve, reject) => {
        showLoader();
        SurveysApi.getSurveysStats()
            .then(response => {
                getSurveysStats.complete({
                    surveysStats: response.value,
                })
            })
            .catch(e => {
                mobileAlert(M("ooops"), responses.msg(e), "error");
                getSurveysStats.fail();
                reject();
            })
            .finally(hideLoader)
    })
});